import type { RowPatch } from '../../lib/tasks';
import { TASK_STATUSES, TASK_STATUS_LABELS, isTaskStatus } from '../../lib/taskStatus';
import { Button, Select } from '../ui';
import { DueDateButton } from './DueDatePicker';
import { CheckIcon } from '../icons';

/**
 * What you can do to a selection, above the list it belongs to.
 *
 * Additive to the filter toolbar rather than replacing it: filtering and
 * selecting are usually the same thought, and swapping the filters out from
 * under someone mid-selection is how you lose the list you were working on.
 *
 * Sticky, because a selection can run past the fold and a bar you have to
 * scroll back up to reach is worse than no bar. It deliberately sits below the
 * task panel's scrim, so opening a task greys it out along with the rest of the
 * page -- the same treatment the failed-create banners already get, and better
 * than a live bulk control floating over a modal.
 *
 * There is no bulk endpoint behind any of this; the page fans each action out
 * over single-task PATCHes and reports what did not land.
 */
export function BulkActionBar({
  count,
  onPatch,
  onClear,
}: {
  count: number;
  onPatch: (patch: RowPatch) => void;
  onClear: () => void;
}) {
  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="sticky top-0 z-30 mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-honey-200 bg-honey-50/95 px-3 py-2 shadow-md backdrop-blur-sm"
    >
      <span aria-live="polite" className="text-sm font-medium text-honey-900">
        {count} selected
      </span>

      <div className="w-40">
        {/* Native, like the panel's own pickers: it gets the platform picker on
            a phone and the platform keyboard behaviour everywhere, which a
            hand-rolled listbox would have to earn back.

            Held at "" with a disabled placeholder so it reads as an action
            rather than as the selection's current status -- there is no single
            current status -- and reset after each change so it can be used
            twice in a row. */}
        <Select
          compact
          value=""
          aria-label="Change status"
          onChange={(event) => {
            const status = event.target.value;
            if (isTaskStatus(status)) onPatch({ status });
          }}
        >
          <option value="" disabled>
            Change status…
          </option>
          {TASK_STATUSES.map((status) => (
            <option key={status} value={status}>
              {TASK_STATUS_LABELS[status]}
            </option>
          ))}
        </Select>
      </div>

      {/* Redundant with the dropdown above and worth the space anyway: this is
          the job the row's circle used to do, and by a distance the most common
          thing anyone does to a batch. */}
      <Button compact variant="primary" onClick={() => onPatch({ status: 'DONE' })}>
        <CheckIcon className="h-3.5 w-3.5" />
        Mark done
      </Button>

      <DueDateButton onPick={(dueDate) => onPatch({ dueDate })} />

      <Button compact variant="ghost" className="ml-auto" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}
